import { getHtmlElementAttributes } from '../../utils/utils'
import pick from 'lodash/pick'
import { COLLAGE_FONTS } from './collageText'
import { COLLAGE_SVG_COMPONENTS } from '../../components/CollageTile/CollageSvg/CollageSvg.consts'

const PHOTO_POSITION_ATTRIBUTES = ['x', 'y', 'width', 'height']
const PHOTO_ATTRIBUTES = [...PHOTO_POSITION_ATTRIBUTES, 'transform']

export function parseLayoutSVG({
  svgContent,
  isText,
  width,
  height,
  backgroundColor,
}) {
  // eslint-disable-next-line no-undef
  const parser = new DOMParser()
  const svg = parser.parseFromString(svgContent, 'image/svg+xml')

  const svgElement = svg.firstChild

  // Fit collage size according to its container if dimensions not given
  svgElement.setAttribute('width', width ? `${width}px` : '100%')
  svgElement.setAttribute('height', height ? `${height}px` : '100%')

  if (backgroundColor) {
    // Add background color to the svg, because there's a difference between the ViewBox ratio and the image ratio (causing black sides on backend)
    svgElement.setAttribute('style', `background-color: ${backgroundColor};`)
  }

  prepareCollageLayout(svgElement)

  if (isText) {
    prepareCollageText(svgElement)
  }

  return svgElement
}

// Remove background color of the layout rectangles because we render them on top of the images (in order to have the rects' stroke)
function prepareCollageLayout(svgElement) {
  const layoutContainer = getCollageLayoutContainer(svgElement)
  // Remove background from layout rectangles
  for (const layoutPhoto of layoutContainer.children) {
    layoutPhoto.removeAttribute('fill')
  }
}

export function getCollageLayoutContainer(layoutElement) {
  return layoutElement.getElementsByClassName(COLLAGE_SVG_COMPONENTS.LAYOUT)[0]
}

export function getLayoutPhotos(layoutContainerElement) {
  return Array.from(layoutContainerElement.querySelectorAll(':scope > rect'))
}

export function getCollageTextContainer(svgElement) {
  return svgElement.getElementsByClassName(COLLAGE_SVG_COMPONENTS.TEXT)[0]
}

// Create an element for using fonts inside an SVG.
// Output example: <defs><style type="text/css">@import url('https://fonts.googleapis.com/css2?family=Bad%20Script&amp;display=swap');</style></defs>
function createFontElement(fontSrc) {
  const fontElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'defs'
  )
  const fontLink = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'style'
  )
  fontLink.setAttribute('type', 'text/css')
  fontLink.textContent = `@import url('${fontSrc}');`
  fontElement.appendChild(fontLink)
  return fontElement
}

function createTextPlaceholderElement() {
  const textElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'g'
  )
  textElement.setAttribute('class', COLLAGE_SVG_COMPONENTS.TEXT)
  return textElement
}

function prepareCollageText(svgElement) {
  // Import fonts
  Object.values(COLLAGE_FONTS).forEach((f) => {
    const fontElement = createFontElement(f.url)
    svgElement.appendChild(fontElement)
  })

  // Add Text placeholder at the end of the Layout parent (so it won't be hidden by masks etc.)
  const layoutContainerElement = getCollageLayoutContainer(svgElement)
  const textPlaceholderElement = createTextPlaceholderElement()
  layoutContainerElement.parentElement.appendChild(textPlaceholderElement)
}

export function getCollagePhotoRectClassName(photoId) {
  return `layout-rect-${photoId}`
}

export function getCollagePhotoClassName(photoId) {
  return `layout-image-${photoId}`
}

export function parseCollagePhotoIdFromClassName(className) {
  return className.replace('layout-rect-', '')
}

// Return a map of photo ids to their positions
export function parseCollagePhotoPositions(layoutElement) {
  const photoElements = Array.from(
    getLayoutPhotos(getCollageLayoutContainer(layoutElement))
  )
  return Object.fromEntries(
    photoElements.map((photo) => {
      const { class: photoId, ...attributes } = getHtmlElementAttributes(photo) // Classnames define the photo ids in the svg
      const positionAttributes = pick(attributes, PHOTO_ATTRIBUTES)
      // Convert PHOTO_POSITION_ATTRIBUTES to numbers
      return [
        photoId,
        Object.entries(positionAttributes).reduce((acc, [key, value]) => {
          if (PHOTO_POSITION_ATTRIBUTES.includes(key)) {
            acc[key] = Number(value)
          } else {
            acc[key] = value
          }
          return acc
        }, {}),
      ]
    })
  )
}
